/* eslint-disable import/no-cycle */
import { useMemo, useState } from 'react';
import { getCompanyIdFromLocalStorage } from '@utils/local-storage';
import { MachineOption, RealTimeFilters } from 'types';
import { SavedMachineGroup } from 'ducks/user/reducers';
import { RealTimePageV2FilterAndSortParams } from '@clean-code/pages/RealTimePageV2/types';

export const AppLocalStorageKeys = {
  GLOBAL_MACHINES_GROUPS: 'GLOBAL_MACHINES_GROUPS',
  GLOBAL_MACHINES_GROUP_MACHINES: 'GLOBAL_MACHINES_GROUP_MACHINES',
  REAL_TIME_FILTERS: 'REAL_TIME_FILTERS',
  MACHINES_SHIFT_SORT_PREFERENCES: 'MACHINES_SHIFT_SORT_PREFERENCES',
  PRODUCTION_ONLY_CURRENT_SHIFTS: 'production-only-current-shifts',
  STOPS_TABLE_COLUMN_ORDER: 'STOPS_TABLE_COLUMN_ORDER',
  REAL_TIME_PAGE_V2_FILTER_AND_SORT_PARAMS:
    'REAL_TIME_PAGE_V2_FILTER_AND_SORT_PARAMS',
  REAL_TIME_PAGE_V2_TOUR_COMPLETED: 'REAL_TIME_PAGE_V2_TOUR_COMPLETED'
};

export class AppLocalStorageValues {
  private companyId?: number;

  constructor(companyId: number | undefined) {
    this.companyId = companyId;
  }

  private getKey(key: string) {
    return `PULSAR#${this.companyId}#${key}`;
  }

  private addOrDeleteItem(
    key: string,
    val:
      | string
      | null
      | undefined
      | MachineOption[]
      | SavedMachineGroup[]
      | number[]
  ) {
    if (val) {
      if (Array.isArray(val)) {
        localStorage.setItem(this.getKey(key), JSON.stringify(val));
        return;
      }
      localStorage.setItem(this.getKey(key), val);
    } else {
      localStorage.removeItem(this.getKey(key));
    }
  }

  private getItemValue(key: string) {
    return localStorage.getItem(this.getKey(key));
  }

  /* Values */

  get globalMachinesGroups() {
    return this.getItemValue(AppLocalStorageKeys.GLOBAL_MACHINES_GROUPS)
      ? JSON.parse(
          this.getItemValue(AppLocalStorageKeys.GLOBAL_MACHINES_GROUPS)!
        )
      : [];
  }

  set globalMachinesGroups(machinesGroups: SavedMachineGroup[]) {
    this.addOrDeleteItem(
      AppLocalStorageKeys.GLOBAL_MACHINES_GROUPS,
      machinesGroups
    );
  }

  get globalMachinesGroupMachines() {
    return this.getItemValue(AppLocalStorageKeys.GLOBAL_MACHINES_GROUP_MACHINES)
      ? JSON.parse(
          this.getItemValue(AppLocalStorageKeys.GLOBAL_MACHINES_GROUP_MACHINES)!
        )
      : [];
  }

  set globalMachinesGroupMachines(
    machines: (MachineOption & {
      machineGroupId: string;
    })[]
  ) {
    this.addOrDeleteItem(
      AppLocalStorageKeys.GLOBAL_MACHINES_GROUP_MACHINES,
      machines
    );
  }

  get realTimeFilters(): RealTimeFilters {
    return this.getItemValue(AppLocalStorageKeys.REAL_TIME_FILTERS)
      ? JSON.parse(this.getItemValue(AppLocalStorageKeys.REAL_TIME_FILTERS)!)
      : {
          showAlertsMachinesOnly: false,
          showStoppedMachinesOnly: false,
          selectedMetric: 'machine_title',
          sortType: 'asc'
        };
  }

  set realTimeFilters(realTimeFilters: RealTimeFilters) {
    this.addOrDeleteItem(
      AppLocalStorageKeys.REAL_TIME_FILTERS,
      JSON.stringify(realTimeFilters)
    );
  }

  get machinesShiftSortPreferences(): {
    metricType: string;
    sortType: 'asc' | 'desc';
  } {
    const key = AppLocalStorageKeys.MACHINES_SHIFT_SORT_PREFERENCES;
    return this.getItemValue(key)
      ? JSON.parse(this.getItemValue(key) ?? '{}')
      : {
          metricType: 'machine_title',
          sortType: 'asc'
        };
  }

  set machinesShiftSortPreferences(sortConfig: {
    metricType: string;
    sortType: 'asc' | 'desc';
  }) {
    this.addOrDeleteItem(
      AppLocalStorageKeys.MACHINES_SHIFT_SORT_PREFERENCES,
      JSON.stringify(sortConfig)
    );
  }

  get stopsTableColumnOrder(): number[] {
    return this.getItemValue(AppLocalStorageKeys.STOPS_TABLE_COLUMN_ORDER)
      ? JSON.parse(
          this.getItemValue(AppLocalStorageKeys.STOPS_TABLE_COLUMN_ORDER)!
        )
      : Array.from(Array(16).keys());
  }

  set stopsTableColumnOrder(columnOrder: number[]) {
    this.addOrDeleteItem(
      AppLocalStorageKeys.STOPS_TABLE_COLUMN_ORDER,
      JSON.stringify(columnOrder)
    );
  }

  get realtimePageV2FilterAndSortParams(): RealTimePageV2FilterAndSortParams {
    return this.getItemValue(
      AppLocalStorageKeys.REAL_TIME_PAGE_V2_FILTER_AND_SORT_PARAMS
    )
      ? JSON.parse(
          this.getItemValue(
            AppLocalStorageKeys.REAL_TIME_PAGE_V2_FILTER_AND_SORT_PARAMS
          )!
        )
      : {
          type: 'realtime',
          direction: 'asc',
          sortBy: 'machineOrder',
          filterStatus: []
        };
  }

  set realtimePageV2FilterAndSortParams(
    params: RealTimePageV2FilterAndSortParams
  ) {
    this.addOrDeleteItem(
      AppLocalStorageKeys.REAL_TIME_PAGE_V2_FILTER_AND_SORT_PARAMS,
      JSON.stringify(params)
    );
  }
}

export default function useAppLocalStorage(companyId?: number | undefined) {
  const companyIdFromLocalStorage = useMemo(
    () => getCompanyIdFromLocalStorage(),
    []
  );
  const [appLocalStorage] = useState(
    new AppLocalStorageValues(companyId ?? companyIdFromLocalStorage)
  );
  return appLocalStorage;
}
